import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EmptyState } from '@/components/base/EmptyState';
import { MobileTabs } from '@/components/base/MobileUi';
import { showToast } from '@/components/base/Toast';
import { confirmModal } from '@/components/base/ConfirmModal';
import { LimitRequestTab } from './components/LimitRequestTab';
import { useStore } from '@/hooks/useStore';
import { useAuth } from '@/hooks/useAuth';
import {
  getContractsByHtx,
  getDebtsByFarmer,
  getFarmerKyc,
  getFarmerRemainingLimit,
  getFarmersByHtx,
  getFieldsByFarmer,
  getLoansByHtx,
  getRiskProfileByFarmer,
  getSeasonsByHtx,
  requestLoanMoreInfo,
} from '@/store';
import type { AdvanceRequest, Farmer } from '@/types';
import FarmerLimitTab from './components/FarmerLimitTab';
import LoanCard from './components/LoanCard';
import LoanDetailModal from './components/LoanDetailModal';
import LoanFilters from './components/LoanFilters';
import LoanNeedMoreModal from './components/LoanNeedMoreModal';
import LoanStats from './components/LoanStats';
import { CreateLoanForFarmerModal } from './components/CreateLoanForFarmerModal';

type TabKey = 'khoan_vay' | 'han_muc_nong_ho' | 'yeu_cau_han_muc';

const TABS: { id: TabKey; label: string; icon: string }[] = [
  { id: 'khoan_vay', label: 'Khoản vay nông hộ', icon: 'ri-file-list-3-line' },
  { id: 'han_muc_nong_ho', label: 'Hạn mức nông hộ', icon: 'ri-user-settings-line' },
  { id: 'yeu_cau_han_muc', label: 'Yêu cầu hạn mức', icon: 'ri-scales-3-line' },
];

function mapNavTab(tab?: string): TabKey {
  if (tab === 'han_muc_nong_ho' || tab === 'dinh_muc') return 'han_muc_nong_ho';
  if (tab === 'yeu_cau_han_muc') return 'yeu_cau_han_muc';
  return 'khoan_vay';
}

/** Approval, disbursement, limit-granting, and Antesco/bank funding are Agrida-
 * internal actions with no accessible endpoint for HTX_STAFF in the real backend —
 * that whole decisioning surface lives in the separate Admin Portal BFF. This page
 * only shows status and the one HTX-accessible action, request-more-info. See task 27
 * notes for the full mapping of what got removed and why. */
export default function HtxLoanPage() {
  useStore();
  const { user, reloadUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // The loan button is the one place an HTX's own eKYC blocks anything (the
  // backend enforces the same rule). Re-read the session first so an approval
  // that just happened in the admin portal is picked up without a re-login.
  const openCreateLoan = async () => {
    let status = user?.kycStatus;
    if (status !== 'ekyc_verified') {
      status = (await reloadUser().catch(() => user))?.kycStatus;
    }
    if (status === 'ekyc_verified') {
      setCreateLoanOpen(true);
      return;
    }
    const pending = status === 'ekyc_pending';
    const goToEkyc = await confirmModal({
      title: pending ? 'eKYC đang chờ duyệt' : 'Cần hoàn tất eKYC',
      message: pending
        ? 'Hồ sơ eKYC của hợp tác xã đang chờ Agrida duyệt. Bạn sẽ tạo được khoản vay ngay sau khi hồ sơ được xác thực.'
        : 'Hợp tác xã cần hoàn tất eKYC và được Agrida duyệt trước khi tạo khoản vay. Các chức năng khác vẫn dùng bình thường.',
      confirmText: pending ? 'Xem trạng thái' : 'Làm eKYC ngay',
      cancelText: 'Để sau',
      variant: 'primary',
    });
    if (goToEkyc) navigate('/ekyc');
  };
  const navState = location.state as { tab?: string; farmerId?: string } | null;
  const loans = getLoansByHtx(user?.htxId);
  const farmers = getFarmersByHtx(user?.htxId);
  const seasons = getSeasonsByHtx(user?.htxId);
  const contracts = getContractsByHtx(user?.htxId);
  // `getLoansByHtx` composes a fresh array (flatMap over farmers) on every call, so
  // `loans` never has a stable identity across renders even when its contents are
  // unchanged. Memoizing on a content signature — rather than on `loans` itself —
  // keeps `moneyLoans` referentially stable, which is what stops LoanFilters' effect
  // (keyed off this array) from re-firing every render and looping forever.
  const loansSignature = loans.map((loan) => `${loan.id}:${loan.status}:${loan.soTien}`).join('|');
  const moneyLoans = useMemo(() => loans.filter((loan) => loan.loai === 'Tiền'), [loansSignature]); // eslint-disable-line react-hooks/exhaustive-deps
  const [tab, setTab] = useState<TabKey>(mapNavTab(navState?.tab));
  const [filtered, setFiltered] = useState<AdvanceRequest[]>(moneyLoans);
  const [detailItem, setDetailItem] = useState<AdvanceRequest | null>(null);
  const [needMoreItem, setNeedMoreItem] = useState<AdvanceRequest | null>(null);
  const [createLoanOpen, setCreateLoanOpen] = useState(false);

  const farmerMap = useMemo(() => Object.fromEntries(farmers.map((farmer) => [farmer.id, farmer])), [farmers]);
  const visibleLoans = filtered;

  const getFarmer = (id: string): Farmer | undefined => farmerMap[id];
  const getFarmerName = (id: string) => farmerMap[id]?.name || id;
  // An HTX-direct loan's `farmerId` field is actually the HTX's own id (see
  // AdvanceRequest.borrowerType) — falling through to getFarmerName for one
  // would show the raw ObjectId as if it were a farmer's name.
  const getBorrowerName = (loan: AdvanceRequest) => (loan.borrowerType === 'HTX' ? 'HTX của bạn (vay trực tiếp)' : getFarmerName(loan.farmerId));
  const getSeason = (id?: string) => seasons.find((season) => season.id === id);
  const getSeasonName = (id?: string) => getSeason(id)?.name || '';
  const getFieldName = (farmerId: string, seasonId?: string) => {
    const season = getSeason(seasonId);
    return season ? getFieldsByFarmer(farmerId).find((field) => field.id === season.fieldId)?.name || '' : '';
  };

  const handleNeedMore = useCallback(async (content: string, documentName: string, dueDate: string) => {
    if (!needMoreItem) return;
    const note = documentName ? `${content} | Tài liệu: ${documentName}${dueDate ? ` | Hạn: ${dueDate}` : ''}` : content;
    try {
      await requestLoanMoreInfo(needMoreItem.id, note, needMoreItem.farmerId);
      showToast('Đã gửi yêu cầu bổ sung.', 'success');
    } catch {
      showToast('Không thể gửi yêu cầu, vui lòng thử lại', 'error');
    }
    setNeedMoreItem(null);
  }, [needMoreItem]);

  return (
    <div className="mobile-page space-y-4">
      <header className="min-w-0">
        <h2 className="text-lg font-bold text-slate-900">Vay & Hạn mức</h2>
        <p className="metric-caption mt-0.5 text-[11px] text-slate-400">Theo dõi khoản vay và hạn mức nông hộ</p>
      </header>

      <MobileTabs<TabKey> tabs={TABS} value={tab} onChange={setTab} />

      {tab === 'han_muc_nong_ho' && <FarmerLimitTab initialFarmerId={navState?.farmerId} />}
      {tab === 'yeu_cau_han_muc' && <LimitRequestTab htxId={user?.htxId} />}
      {tab === 'khoan_vay' && (
        <div className="space-y-4">
          <LoanStats loans={moneyLoans} />
          <button
            type="button"
            onClick={() => void openCreateLoan()}
            className="w-full rounded-xl border border-dashed border-agrida-300 bg-agrida-50 py-2.5 text-sm font-semibold text-agrida-700"
          >
            + Tạo khoản vay hộ nông hộ
          </button>
          <LoanFilters loans={moneyLoans} onFilterChange={setFiltered} />
          {visibleLoans.length === 0 ? (
            <EmptyState icon="ri-file-list-3-line" title="Chưa có yêu cầu vay" description="Yêu cầu mới từ nông hộ sẽ xuất hiện tại đây." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {visibleLoans.map((loan) => {
                const isHtxBorrower = loan.borrowerType === 'HTX';
                const farmer = isHtxBorrower ? undefined : getFarmer(loan.farmerId);
                const kyc = farmer ? getFarmerKyc(farmer) : undefined;
                return <LoanCard key={loan.id} loan={loan} farmer={farmer || fallbackFarmer(loan.farmerId, getBorrowerName(loan))} farmerName={getBorrowerName(loan)} seasonName={getSeasonName(loan.seasonId)} riskMucDo={isHtxBorrower ? undefined : (getRiskProfileByFarmer(loan.farmerId)?.mucDo || kyc?.riskLevel)} creditScore={kyc?.creditScore} hanMucConLai={isHtxBorrower ? undefined : getFarmerRemainingLimit(loan.farmerId)} onViewDetail={() => setDetailItem(loan)} />;
              })}
            </div>
          )}
        </div>
      )}

      {detailItem && (
        <LoanDetailModal
          loan={detailItem}
          farmer={getFarmer(detailItem.farmerId) || fallbackFarmer(detailItem.farmerId, getBorrowerName(detailItem))}
          farmerName={getBorrowerName(detailItem)}
          seasonName={getSeasonName(detailItem.seasonId)}
          fieldName={getFieldName(detailItem.farmerId, detailItem.seasonId)}
          debts={getDebtsByFarmer(detailItem.farmerId)}
          contracts={contracts}
          onClose={() => setDetailItem(null)}
          onNeedMore={() => { setNeedMoreItem(detailItem); setDetailItem(null); }}
          onViewContract={() => navigate('/htx/hop-dong')}
        />
      )}
      {needMoreItem && <LoanNeedMoreModal loan={needMoreItem} farmerName={getBorrowerName(needMoreItem)} onClose={() => setNeedMoreItem(null)} onNeedMore={handleNeedMore} />}
      {createLoanOpen && (
        <CreateLoanForFarmerModal
          htxId={user?.htxId}
          farmers={farmers}
          onClose={() => setCreateLoanOpen(false)}
          onCreated={() => setCreateLoanOpen(false)}
        />
      )}
    </div>
  );
}

function fallbackFarmer(id: string, name: string): Farmer {
  return { id, code: '', name, phone: '', address: '', tinhThanh: '', huyen: '', xa: '', dienTich: 0, soThanhVien: 0, loaiCay: '', ngayBatDau: '', status: 'Hoạt động', membershipStatus: 'Đã xác nhận' };
}
